import { useEffect, useRef, useState } from "react";

/**
 * Animates a number from 0 to `target` when the returned ref enters the viewport.
 * Returns [ref, displayValue].
 *
 * @param {number} target  final number
 * @param {object} opts    { duration (ms), suffix, prefix, decimals }
 */
export function useCountUp(target, opts = {}) {
  const { duration = 1400, suffix = "", prefix = "", decimals = 0 } = opts;
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setValue(target);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const tick = (now) => {
              const p = Math.min((now - start) / duration, 1);
              // easeOutCubic
              const eased = 1 - Math.pow(1 - p, 3);
              setValue(target * eased);
              if (p < 1) requestAnimationFrame(tick);
              else setValue(target);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration]);

  const display = `${prefix}${value.toFixed(decimals)}${suffix}`;
  return [ref, display, value];
}
